import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth';
import logo from '../../assets/logo.png';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'verify' | 'password'>('email');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (loginError || message) {
      const timer = setTimeout(() => {
        setLoginError('');
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loginError, message]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Invalid email format';
    if (!password) errors.password = 'Password is required';
    else if (password.length < 6) errors.password = 'Password must be at least 6 characters';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateResetForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (resetStep === 'email' && !resetEmail) {
      errors.resetEmail = 'Email is required';
    } else if (resetStep === 'email' && !/\S+@\S+\.\S+/.test(resetEmail)) {
      errors.resetEmail = 'Invalid email format';
    }
    if (resetStep === 'verify' && !resetCode) {
      errors.resetCode = 'Verification code is required';
    }
    if (resetStep === 'password') {
      if (!newPassword) errors.newPassword = 'New password is required';
      else if (newPassword.length < 6) errors.newPassword = 'Password must be at least 6 characters';
      if (!confirmPassword) errors.confirmPassword = 'Please confirm your password';
      else if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError('');
    setMessage('');
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const success = await login(email, password);
      if (success) {
        setMessage(' Login successful. Redirecting...');
        setTimeout(() => navigate('/'), 1500);
      } else {
        setLoginError('Invalid email or password. Please try again.');
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendResetCode = async () => {
    if (!validateResetForm()) return;
    
    setIsSubmitting(true);
    try {
      await authService.sendResetCode(resetEmail);
      setMessage('✅ Verification code sent to your email.');
      setResetStep('verify');
    } catch (err: any) {
      setMessage(err?.message || 'Email not found. Please check and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (!validateResetForm()) return;
    
    setIsSubmitting(true);
    try {
      // You might want to verify the code with your backend here
      setResetStep('password');
      setMessage('');
    } catch (err: any) {
      setMessage(err?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validateResetForm()) return;
    
    setIsSubmitting(true);
    try {
      await authService.resetPassword(resetEmail, resetCode, newPassword);
      setMessage('✅ Password reset successful. You can now log in.');
      setTimeout(() => {
        setShowReset(false);
        setResetStep('email');
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err: any) {
      setMessage(err?.message || 'Invalid or expired code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row items-center justify-center bg-gradient-to-br from-blue-50 via-white to-gray-100 px-4 sm:px-6 py-8 gap-8 transition-all duration-300">
      {/* --- Video & Info Section --- */}
      <div className="w-full lg:w-1/2 flex flex-col items-center text-center space-y-6 px-2 sm:px-6">
        <div className="space-y-3">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            How to Login & Use Your Account
          </h1>
          <p className="text-gray-600 max-w-md text-sm md:text-base leading-relaxed">
            Watch this short guide to understand how to log in, reset your password, 
            and navigate your dashboard with ease.
          </p>
        </div>

        <div className="w-full max-w-xl aspect-video rounded-2xl overflow-hidden shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl hover:scale-[1.02]">
          <iframe
            src="https://www.youtube.com/embed/IFsGvW0UvAc"
            title="Login Tutorial"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm">
          <p className="text-blue-700 text-xs sm:text-sm">
            💡 <span className="font-semibold">Pro Tip:</span> If you face login issues, 
            verify your email or reset your password below.
          </p>
        </div>
      </div>

      {/* --- Login / Reset Form --- */}
      <div className="w-full lg:w-1/3 flex justify-center">
        <div className="bg-white shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-md border border-gray-100 transition-all duration-300 hover:shadow-2xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src={logo} 
              alt="App Logo" 
              className="h-14 w-auto object-contain transition-transform duration-300 hover:scale-105" 
            />
          </div>

          {/* Header */}
          <div className="text-center mb-7">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Welcome Back
            </h2>
            <p className="text-gray-600 text-sm">
              Sign in to continue or{' '}
              <Link
                to="/register"
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200 hover:underline"
              >
                create a new account
              </Link>
            </p>
          </div>

          {/* Alerts */}
          {(error || message || loginError) && (
            <div
              className={`${
                error || loginError
                  ? 'bg-red-50 border-red-300 text-red-800'
                  : 'bg-green-50 border-green-300 text-green-800'
              } border px-4 py-3 rounded-lg text-sm mb-5 transition-all duration-500 flex items-start space-x-2`}
            >
              <span className="flex-shrink-0 mt-0.5">
                {error || loginError ? '⚠️' : '✅'}
              </span>
              <span className="flex-1">{error || loginError || message}</span>
            </div>
          )}

          {/* --- Login Form --- */}
          {!showReset ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 border ${
                    validationErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  } rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validationErrors.email) {
                      setValidationErrors(prev => ({ ...prev, email: '' }));
                    }
                  }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                {validationErrors.email && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {validationErrors.email}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 border ${
                    validationErrors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  } rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (validationErrors.password) {
                      setValidationErrors(prev => ({ ...prev, password: '' }));
                    }
                  }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                {validationErrors.password && (
                  <p className="text-xs text-red-500 mt-1 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {validationErrors.password}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReset(true);
                    setMessage('');
                    setLoginError('');
                    setValidationErrors({});
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200 hover:underline"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading || isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          ) : (
            /* --- Password Reset --- */
            <div className="space-y-5 animate-fadeIn">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-gray-800 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Reset Password
                </h3>
                <button
                  onClick={() => {
                    setShowReset(false);
                    setMessage('');
                    setResetStep('email');
                    setLoginError('');
                    setValidationErrors({});
                  }}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close reset password"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Reset Progress Indicator */}
              <div className="flex justify-between items-center mb-6 relative">
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2 -z-10"></div>
                <div 
                  className={`absolute top-1/2 left-0 h-0.5 bg-blue-600 -translate-y-1/2 transition-all duration-500 ${
                    resetStep === 'email' ? 'w-1/3' : resetStep === 'verify' ? 'w-2/3' : 'w-full'
                  }`}
                ></div>
                {['email', 'verify', 'password'].map((step, index) => (
                  <div key={step} className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                      resetStep === step || (step === 'verify' && resetStep === 'password') || (step === 'email' && (resetStep === 'verify' || resetStep === 'password'))
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="text-xs mt-1 text-gray-600 capitalize">{step}</span>
                  </div>
                ))}
              </div>

              {resetStep === 'email' && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enter your registered email
                    </label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      className={`w-full px-4 py-3 border ${
                        validationErrors.resetEmail ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      } rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                      value={resetEmail}
                      onChange={(e) => {
                        setResetEmail(e.target.value);
                        if (validationErrors.resetEmail) {
                          setValidationErrors(prev => ({ ...prev, resetEmail: '' }));
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    {validationErrors.resetEmail && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {validationErrors.resetEmail}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSendResetCode}
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </div>
              )}

              {resetStep === 'verify' && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enter verification code
                    </label>
                    <input
                      type="text"
                      placeholder="6-digit code"
                      className={`w-full px-4 py-3 border ${
                        validationErrors.resetCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      } rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200`}
                      value={resetCode}
                      onChange={(e) => {
                        setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        if (validationErrors.resetCode) {
                          setValidationErrors(prev => ({ ...prev, resetCode: '' }));
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    {validationErrors.resetCode && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {validationErrors.resetCode}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setResetStep('email')}
                      className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-all duration-200 font-medium"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleVerifyResetCode}
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-3 rounded-lg font-medium shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify Code'}
                    </button>
                  </div>
                </div>
              )}

              {resetStep === 'password' && (
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New password
                    </label>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      className={`w-full px-4 py-3 border ${
                        validationErrors.newPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      } rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (validationErrors.newPassword || validationErrors.confirmPassword) {
                          setValidationErrors(prev => ({ 
                            ...prev, 
                            newPassword: '',
                            confirmPassword: '' 
                          }));
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    {validationErrors.newPassword && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {validationErrors.newPassword}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      className={`w-full px-4 py-3 border ${
                        validationErrors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      } rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200`}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (validationErrors.confirmPassword) {
                          setValidationErrors(prev => ({ ...prev, confirmPassword: '' }));
                        }
                      }}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    {validationErrors.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {validationErrors.confirmPassword}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setResetStep('verify')}
                      className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-all duration-200 font-medium"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={isSubmitting}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02]"
                    >
                      {isSubmitting ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;